import { useState } from 'react'

import type { GuidedPurchaseJourneyStep } from '../Steps/useQuestionSteps'

export const useGuidedPurchaseNavigation = (
  steps: GuidedPurchaseJourneyStep[],
  skippedSteps: Set<string>
) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [screenHistory, setScreenHistory] = useState([0])

  const resolveIndex = (name: string) => steps.findIndex((s) => s.name === name)

  const next = () => {
    let nextPageIndex = currentPageIndex
    do {
      nextPageIndex++
    } while (nextPageIndex < steps.length - 1 && skippedSteps.has(steps[nextPageIndex].name))

    if (nextPageIndex < 0 || nextPageIndex > steps.length - 1) {
      return
    }

    setCurrentPageIndex(nextPageIndex)
    setScreenHistory((prev) => [...prev, nextPageIndex])
  }

  const back = () => {
    const previousPage = screenHistory[screenHistory.length - 2]
    if (previousPage !== undefined) {
      setCurrentPageIndex(previousPage)
      setScreenHistory((prev) => prev.slice(0, -1))
    }
  }

  const goToLastPage = () => {
    if (currentPageIndex === steps.length - 1) {
      return
    }
    setCurrentPageIndex(steps.length - 1)
    setScreenHistory((prev) => [...prev, steps.length - 1])
  }

  const canGoBack = screenHistory.length > 1
  const canGoNext = (() => {
    const currentStep = steps[currentPageIndex]
    const nextPageIndex =
      currentStep?.nextStep !== undefined
        ? resolveIndex(currentStep.nextStep)
        : currentPageIndex + 1
    return nextPageIndex >= 0 && nextPageIndex <= steps.length - 1
  })()
  const isLastPage = currentPageIndex === steps.length - 1
  const currentStep = steps[currentPageIndex]
  const isCurrentStepRequired = !!currentStep?.required
  const isNextDisabled = !currentStep?.isAnswered

  return {
    component: steps[currentPageIndex]?.component || null,
    trackingCallback: steps[currentPageIndex]?.trackingCallback,
    title: steps[currentPageIndex]?.title || `Step ${currentPageIndex + 1}`,
    currentPageIndex,
    next,
    back,
    canGoBack,
    canGoNext,
    isLastPage,
    isCurrentStepRequired,
    isNextDisabled,
    goToLastPage,
  }
}
